import { useState, useCallback, useEffect } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Drawer from '@mui/material/Drawer';
import Tooltip from '@mui/material/Tooltip';
import { useTheme, useColorScheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import {
  Button,
  FormControl,
  Select,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  MenuItem,
} from '@mui/material';
import { RouterLink } from 'src/routes/components';
import { Iconify } from 'src/components/iconify';

import { paths } from 'src/routes/paths';
import { useRouter, usePathname } from 'src/routes/hooks';

import { _mock } from 'src/_mock';
import { varAlpha } from 'src/theme/styles';

import { Label } from 'src/components/label';
import { Scrollbar } from 'src/components/scrollbar';
import { AnimateAvatar } from 'src/components/animate';
import { BaseOption } from 'src/components/settings/drawer/base-option';
import { useSettingsContext } from 'src/components/settings';
import { useAuthContext, useMockedUser } from 'src/auth/hooks';

import { UpgradeBlock } from './nav-upgrade';
import { AccountButton } from './account-button';
import { SignOutButton } from './sign-out-button';
import { select } from 'src/theme/core/components/select';
import { get_timezones, update_time_zone } from 'src/auth/context/jwt/program';

// ----------------------------------------------------------------------

export function AccountDrawer({ data = [], sx, ...other }) {
  const theme = useTheme();

  const settings = useSettingsContext();
  const { mode, setMode } = useColorScheme();

  const { user } = useAuthContext();

  const router = useRouter();

  const pathname = usePathname();

  // const { user } = useMockedUser();

  const [open, setOpen] = useState(false);

  const handleOpenDrawer = useCallback(() => {
    setOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setOpen(false);
  }, []);

  const handleClickItem = useCallback(
    (path) => {
      handleCloseDrawer();
      router.push(path);
    },
    [handleCloseDrawer, router]
  );

  const renderAvatar = (
    <AnimateAvatar
      width={96}
      slotProps={{
        avatar: { src: user?.avatar, alt: user?.name },
        overlay: {
          border: 2,
          spacing: 3,
          color: `linear-gradient(135deg, ${varAlpha(theme.vars.palette.primary.mainChannel, 0)} 25%, ${theme.vars.palette.primary.main} 100%)`,
        },
      }}
    >
      {user?.name?.charAt(0).toUpperCase()}
    </AnimateAvatar>
  );

  const [timeZones, settimeZones] = useState();

  const [selectedTimezone, setselectedTimezone] = useState(user?.timezone?.id || '');

  const [loading, setloading] = useState(false);

  const [supportExpanded, setSupportExpanded] = useState(false);

  const handleSupportChange = () => {
    setSupportExpanded(!supportExpanded);
  };

  const updateTime = async (timezone) => {
    if (!timezone) {
      return;
    }

    setloading(true);
    try {
      const res = await update_time_zone(timezone);

      localStorage.setItem('user', JSON.stringify(res?.data));
    } catch (error) {
      console.error('Error updating time zone:', error);
    } finally {
      setloading(false);
    }
  };

  useEffect(() => {
    const fetchPrograms = async () => {
      setloading(true);
      try {
        const res = await get_timezones();

        settimeZones(
          res?.data?.data || [
            {
              id: 1,
              name: 'America/New_York',
              label: 'Eastern Time (US & Canada)',
              gmt_offset: 'GMT-05:00',
              gmt_name: 'GMT-05:00 Eastern Time',
              status: '22',
              created_at: '2026-01-07T15:15:02.000000Z',
              updated_at: '2026-01-07T15:15:02.000000Z',
              deleted_at: null,
            },
            {
              id: 2,
              name: 'America/Chicago',
              label: 'Central Time (US & Canada)',
              gmt_offset: 'GMT-06:00',
              gmt_name: 'GMT-06:00 Central Time',
              status: '22',
              created_at: '2026-01-07T15:15:02.000000Z',
              updated_at: '2026-01-07T15:15:02.000000Z',
              deleted_at: null,
            },
            {
              id: 3,
              name: 'America/Denver',
              label: 'Mountain Time (US & Canada)',
              gmt_offset: 'GMT-07:00',
              gmt_name: 'GMT-07:00 Mountain Time',
              status: '22',
              created_at: '2026-01-07T15:15:02.000000Z',
              updated_at: '2026-01-07T15:15:02.000000Z',
              deleted_at: null,
            },
            {
              id: 4,
              name: 'America/Los_Angeles',
              label: 'Pacific Time (US & Canada)',
              gmt_offset: 'GMT-08:00',
              gmt_name: 'GMT-08:00 Pacific Time',
              status: '22',
              created_at: '2026-01-07T15:15:02.000000Z',
              updated_at: '2026-01-07T15:15:02.000000Z',
              deleted_at: null,
            },
            {
              id: 5,
              name: 'America/Anchorage',
              label: 'Alaska Time',
              gmt_offset: 'GMT-09:00',
              gmt_name: 'GMT-09:00 Alaska Time',
              status: '22',
              created_at: '2026-01-07T15:15:02.000000Z',
              updated_at: '2026-01-07T15:15:02.000000Z',
              deleted_at: null,
            },
            {
              id: 6,
              name: 'Pacific/Honolulu',
              label: 'Hawaii Time',
              gmt_offset: 'GMT-10:00',
              gmt_name: 'GMT-10:00 Hawaii Time',
              status: '22',
              created_at: '2026-01-07T15:15:02.000000Z',
              updated_at: '2026-01-07T15:15:02.000000Z',
              deleted_at: null,
            },
            {
              id: 7,
              name: 'Asia/Karachi',
              label: 'Pakistan Standard Time',
              gmt_offset: 'GMT+05:00',
              gmt_name: 'GMT+05:00 Pakistan Time',
              status: '22',
              created_at: '2026-01-07T15:15:02.000000Z',
              updated_at: '2026-01-07T15:15:02.000000Z',
              deleted_at: null,
            },
          ]
        );
      } catch (error) {
        console.error('Error fetching programs:', error);
      } finally {
        setloading(false);
      }
    };
    fetchPrograms();
  }, []);

  return (
    <>
      <AccountButton
        onClick={handleOpenDrawer}
        photoURL={user?.avatar}
        displayName={user?.name}
        sx={sx}
        {...other}
      />

      <Drawer
        open={open}
        onClose={handleCloseDrawer}
        anchor="right"
        slotProps={{ backdrop: { invisible: true } }}
        PaperProps={{ sx: { width: 320 } }}
      >
        <IconButton
          onClick={handleCloseDrawer}
          sx={{ top: 12, left: 12, zIndex: 9, position: 'absolute' }}
        >
          <Iconify icon="mingcute:close-line" />
        </IconButton>

        <Scrollbar>
          <Stack alignItems="center" sx={{ pt: 8, pb: 8 }}>
            {renderAvatar}
            <Typography
              variant="subtitle1"
              sx={{
                textAlign: 'center',
                mt: 2,
                whiteSpace: 'normal',
                paddingLeft: 2,
                paddingRight: 2,
              }}
            >
              {user?.name}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }} noWrap>
              {user?.email}
            </Typography>
            <Box sx={{ pt: 2, width: '100%', px: 2.5 }}>
              {user?.role?.id === 3 && user?.gym?.status === 'inactive' ? (
                <></>
              ) : (
                <Button
                  variant="outlined"
                  color="primary"
                  fullWidth
                  component={RouterLink}
                  href={paths.dashboard.general.account}
                  onClick={() => {
                    handleCloseDrawer();
                  }}
                  sx={{
                    mb: 1,
                    borderColor: 'primary.main',
                  }}
                  size="large"
                >
                  Account
                </Button>
              )}
              {user?.role?.id !== 1 && (
                <Accordion
                  expanded={supportExpanded}
                  onChange={handleSupportChange}
                  sx={{
                    mt: 2,
                    boxShadow: 'none',
                    border: '1px solid',
                    borderColor: 'primary.main',
                    '&.MuiAccordion-root': {
                      margin: 0,
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<Iconify icon="mingcute:down-line" />}
                    sx={{
                      minHeight: 48,
                      padding: '0px 16px',
                      color: 'primary.main',
                      '&.MuiAccordionSummary-root': {
                        margin: 0,
                      },
                    }}
                  >
                    <Typography sx={{ fontWeight: 700, color: 'inherit', fontSize: '0.9375rem' }}>
                      Support
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    {/* FAQs */}
                    <Box
                      component={RouterLink}
                      href={paths.dashboard.general.faq}
                      onClick={() => {
                        setSupportExpanded(false);
                        handleCloseDrawer();
                      }}
                      sx={{
                        display: 'block',
                        px: 2,
                        py: 1.5,
                        color: 'text.primary',
                        textDecoration: 'none',
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                        cursor: 'pointer',
                      }}
                    >
                      <Typography variant="body2">FAQs</Typography>
                    </Box>

                    {/* FAQs History */}
                    <Box
                      component={RouterLink}
                      href={paths.dashboard.general.faq_history}
                      onClick={() => {
                        setSupportExpanded(false);
                        handleCloseDrawer();
                      }}
                      sx={{
                        display: 'block',
                        px: 2,
                        py: 1.5,
                        color: 'text.primary',
                        textDecoration: 'none',
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                        cursor: 'pointer',
                      }}
                    >
                      <Typography variant="body2">FAQs History</Typography>
                    </Box>

                    {/* Feedback */}
                    <Box
                      component={RouterLink}
                      href={paths.dashboard.general.feedback}
                      onClick={() => {
                        setSupportExpanded(false);
                        handleCloseDrawer();
                      }}
                      sx={{
                        display: 'block',
                        px: 2,
                        py: 1.5,
                        color: 'text.primary',
                        textDecoration: 'none',
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                        cursor: 'pointer',
                      }}
                    >
                      <Typography variant="body2">Feedback</Typography>
                    </Box>

                    {/* Privacy Policy */}
                    <Box
                      component={RouterLink}
                      href={paths.dashboard.general.privacy_policy}
                      onClick={() => {
                        setSupportExpanded(false);
                        handleCloseDrawer();
                      }}
                      sx={{
                        display: 'block',
                        px: 2,
                        py: 1.5,
                        color: 'text.primary',
                        textDecoration: 'none',
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                        cursor: 'pointer',
                      }}
                    >
                      <Typography variant="body2">Privacy Policy</Typography>
                    </Box>

                    {/* Cookie Policy */}
                    <Box
                      component={RouterLink}
                      href={paths.dashboard.general.cookie_policy}
                      onClick={() => {
                        setSupportExpanded(false);
                        handleCloseDrawer();
                      }}
                      sx={{
                        display: 'block',
                        px: 2,
                        py: 1.5,
                        color: 'text.primary',
                        textDecoration: 'none',
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                        cursor: 'pointer',
                      }}
                    >
                      <Typography variant="body2">Cookie Policy</Typography>
                    </Box>

                    {/* Terms and Conditions */}
                    <Box
                      component={RouterLink}
                      href={paths.dashboard.general.terms_and_conditions}
                      onClick={() => {
                        setSupportExpanded(false);
                        handleCloseDrawer();
                      }}
                      sx={{
                        display: 'block',
                        px: 2,
                        py: 1.5,
                        color: 'text.primary',
                        textDecoration: 'none',
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                        cursor: 'pointer',
                      }}
                    >
                      <Typography variant="body2">Terms and Conditions</Typography>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}
              <BaseOption
                sx={{ mt: 2, width: '100%' }}
                label="Dark mode"
                icon="moon"
                selected={settings.colorScheme === 'dark'}
                onClick={() => {
                  settings.onUpdateField('colorScheme', mode === 'light' ? 'dark' : 'light');
                  setMode(mode === 'light' ? 'dark' : 'light');
                }}
              />
            </Box>

            {user?.role?.id === 6 && (
              <Box sx={{ pt: 2, width: '100%', px: 2.5 }}>
                <Typography variant="body2" color="text.secondary" mb={1}>
                  Time Zone:
                </Typography>
                <FormControl size="small" sx={{ minWidth: 80 }} fullWidth>
                  <Select
                    value={selectedTimezone}
                    onChange={(e) => {
                      setselectedTimezone(e.target.value);
                      updateTime(e.target.value);
                    }}
                    disabled={loading}
                    displayEmpty
                  >
                    {timeZones?.map((program) => (
                      <MenuItem key={program.trail_id} value={program?.id.toString()}>
                        {program.name} ({program.gmt_offset})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
          </Stack>
        </Scrollbar>

        <Box sx={{ p: 2.5 }}>
          <SignOutButton onClose={handleCloseDrawer} />
        </Box>
      </Drawer>
    </>
  );
}
